/**
 * ThemeProvider
 *
 * - Reads active theme from useThemeStore
 * - Applies body[data-theme] so CSS variable tokens swap
 * - Wraps children in the matching MUI ThemeProvider + CssBaseline
 * - Dev builds: renders a floating ThemeToggle in bottom-right for quick
 *   verification. Production users set the theme from Account Settings.
 */
import { useLayoutEffect, type ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline, Box } from '@mui/material';
import { useThemeStore } from '@/app/stores/themeStore';
import { ThemeToggle } from '@/app/chrome/ThemeToggle';
import { MUI_THEMES } from './muiThemes';

interface Props {
  children: ReactNode;
}

export const ThemeProvider: React.FC<Props> = ({ children }) => {
  const theme = useThemeStore((s) => s.theme);

  // Apply body[data-theme] synchronously before paint so CSS tokens never
  // lag behind the MUI palette swap. No cleanup — body must always carry
  // a valid theme attribute while the app is mounted (otherwise var() falls
  // back to the body:not([data-theme]) rules which only define light
  // fallbacks, causing dark/legacy to render with light token values).
  useLayoutEffect(() => {
    document.body.dataset.theme = theme;
  }, [theme]);

  return (
    <MuiThemeProvider theme={MUI_THEMES[theme]}>
      <CssBaseline />
      {children}
      {import.meta.env.DEV && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            bgcolor: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-panel)',
            p: 0.5,
          }}
        >
          <ThemeToggle />
        </Box>
      )}
    </MuiThemeProvider>
  );
};
